/* eslint-disable */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import { cloudElementsColumns, ctxrInterfacesColumns, getReflectorDetailsSections } from './ReflectorDetailsData';
import { transformReflectorDetails, transformStandaloneIamPermissions } from '../../../helpers/formattersShapedData';
import CopyableValue from '@shared/components/CopyableValue';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/IconEdit_KE.svg';
import { ReactComponent as IconCheck } from '../../../../shared/assets/Icons/IconCheckSquared.svg';
import { ReactComponent as IconCancel } from '../../../../shared/assets/Icons/IconCrossSquare.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconRefresh } from '../../../../shared/assets/Icons/IconRefreshV2.svg';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import IAMPermissionsModal from './IAMPermisions/IAMPermissionsModal';
import { Reflector } from '../ReflectorData';
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import LicenseGate from '../../shared/LicenseGate';
import { useLicenseGate } from '../../shared/useLicenseGate';
import { getReflectorDetails, getReflectorBgpConfig, getCloudPropagationRules, getCloudPropagationRulesByCluster, getReflectorIamPermissions, updateReflectorName, updateReflectorTags } from '../request/request';
import { EditableTagList } from 'kontrolEdge/components/Shared/NameTagEdit/EditableTagList';
import BgpDetailsPage from '../BgpDetails/BgpDetailsPage';
import CloudPropagationPage from '../CloudPropagation/CloudPropagationPage';
import { isVersionBelow, NEXTHOP_RETRY_MIN_VERSION, NEXTHOP_WATCH_MIN_VERSION } from '../../../helpers/cloudPropagationHelpers';
import CloudElementsPage, { CloudElementsPageProps } from '../CloudElements/CloudElementsPage';
import { CLOUD_ELEMENTS_SUGGESTED_KEYS_REFLECTOR } from '../CloudElements/CloudElementsData';
import SyslogPage from '../Syslog/SyslogPage';
import { IAMPermission } from './IAMPermisions/IAMPermisionsModalData';
import { ReactComponent as IconWarning } from '../../../../shared/assets/Icons/Icon_Warning.svg';
import { cloudPropagationColumns } from '../CloudPropagation/CloudPropagationData';
import ClusterModal from '../Cluster/ClusterModal/ClusterModal';
import { createCluster } from '../Cluster/request/request';
import axios from 'axios';
import { AxiosError } from 'axios';
import EmptyItems from '@shared/components/EmptyItems';
import { filterInterfaces } from '@shared/components/utils/searchUtils';
import { DataStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { TableSkeleton, SkeletonLine } from '@ui-components/Skeleton/GenericSkeleton';
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from '../../../helpers/tableViewport';

/** SideNav + detail tabs + details cards above the interfaces table (CTX-331). */
const INTERFACES_CHROME_PX = 480;

// Using centralized mapAvailabilityToStatus from formattersShapedData

const TABS: TabOption[] = [
  { label: 'Details', value: 'Details' },
  { label: 'BGP', value: 'BGP' },
  { label: 'Cloud Propagation', value: 'Cloud Propagation' },
  { label: 'Cloud Elements', value: 'Cloud Elements' },
  { label: 'Syslog', value: 'Syslog' },
];

type LocationState = { selectedReflectorRow?: Reflector };

const ClusterConfigAlert = ({ clusterName, clusterId }: { clusterName: string; clusterId?: string }) => {
  const navigate = useNavigate();
  
  const handleClusterClick = () => {
    if (clusterId) {
      navigate(`/reflector-v2/management/clusters/details/${clusterId}`);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-4 flex items-start gap-2 my-4">
      <IconWarning className="w-5 h-5 text-blue-700 mt-1" />
      <div>
        <div className="font-semibold text-blue-700">Cluster Configuration Required</div>
        <div className="text-blue-700 text-sm">
          This reflector is part of cluster{' '}
          {clusterId ? (
            <span 
              className="underline font-medium cursor-pointer hover:text-blue-800"
              onClick={handleClusterClick}
            >
              {clusterName}
            </span>
          ) : (
            <span className="underline font-medium">{clusterName}</span>
          )}
          . Configuration must be done at the cluster level.
        </div>
      </div>
    </div>
  );
};

function getIamTagState(permissions: IAMPermission[]) {
  if (!permissions || !Array.isArray(permissions) || permissions.length === 0) return { label: '-', variant: 'neutral' };
  const allAllowed = permissions.every(p => p.enabled);
  const allDenied = permissions.every(p => !p.enabled);
  if (allAllowed) return { label: 'Allowed', variant: 'success' };
  if (allDenied) return { label: 'Denied', variant: 'error' };
  return { label: 'Degraded', variant: 'warning' };
}

// Helper to determine if cluster config alert should be shown
function isClusterConfig(reflector?: Reflector) {
  if (!reflector) return false;
  
  // Check if reflector has a cluster name (not empty, not dash, not undefined)
  const hasClusterName = reflector.cluster && 
    reflector.cluster !== '—' && 
    reflector.cluster !== '-' && 
    reflector.cluster !== '' && 
    reflector.cluster !== 'undefined';
  
  // Check if reflector has a clusterId
  const hasClusterId = reflector.clusterId && 
    reflector.clusterId !== '—' && 
    reflector.clusterId !== '-' && 
    reflector.clusterId !== '' && 
    reflector.clusterId !== 'undefined';
  
  return hasClusterName || hasClusterId;
}

// Helper to check if we're on a cluster route
function isClusterRoute() {
  return window.location.pathname.includes('/clusters/details/');
}

// Helper to format ID for standalone reflector mode: CTXR-(last 6 digits from subId)
function formatReflectorId(subId?: string): string {
  if (!subId) return '';
  // Get last 6 digits from subId
  const last6Digits = subId.slice(-6);
  return `CTXR-${last6Digits}`;
}

function ReflectorDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state: any) => state.selection);

  // Keyed on the route id only — never on `activeTab` — so switching between BGP, Cloud
  // Propagation and Syslog reuses one verdict instead of re-requesting it per tab (CTX-346).
  // The route param rather than `reflector?.id` so the verdict resolves on mount, without
  // waiting for the details fetch.
  const licenseGate = useLicenseGate({ scope: 'instance', id });
  const [activeTab, setActiveTab] = useState<string>('Details');
  const { selectedReflectorRow: row } = (location.state as LocationState) ?? {};
  const [reflector, setReflector] = useState<Reflector | undefined>(row);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);

  // Details tab: first load = overlay; secondary/refresh = skeletons
  const detailsHasStartedLoadRef = useRef(false);
  const detailsHasCompletedFirstLoadRef = useRef(false);
  if (detailsLoading) detailsHasStartedLoadRef.current = true;
  if (!detailsLoading && detailsHasStartedLoadRef.current) detailsHasCompletedFirstLoadRef.current = true;
  const isDetailsFirstLoad = detailsLoading && !detailsHasCompletedFirstLoadRef.current;
  const [shouldRefreshAfterClusterCreation, setShouldRefreshAfterClusterCreation] = useState(false);

  const [bgpConfig, setBgpConfig] = useState(null);
  const [bgpLoading, setBgpLoading] = useState(false);
  const [bgpError, setBgpError] = useState<string | null>(null);

  const bgpPageConfig = React.useMemo(() => {
    return bgpConfig && typeof bgpConfig === "object" ? { ...(bgpConfig as object) } : {};
  }, [bgpConfig]);

  const [cloudPropagationRules, setCloudPropagationRules] = useState<any>([]);
  const [cloudPropagationLoading, setCloudPropagationLoading] = useState(false);
  const [cloudPropagationError, setCloudPropagationError] = useState<string | null>(null);
  const [clusterIdFromResponse, setClusterIdFromResponse] = useState<number | undefined>(undefined);

  const [isIamModalOpen, setIsIamModalOpen] = useState(false);
  const [iamPermissions, setIamPermissions] = useState<IAMPermission[]>([]);
  const [iamPermissionsLoading, setIamPermissionsLoading] = useState(false);
  const [iamPermissionsError, setIamPermissionsError] = useState<string | null>(null);

  // Editable state for Name
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(row?.name || '');

  // Build detailsSections dynamically from reflector data
  const detailsSections = getReflectorDetailsSections(reflector);

  const fetchIamPermissions = async () => {
    if (!reflector?.id) return;
    
    setIamPermissionsLoading(true);
    setIamPermissionsError(null);
    try {
      const token = await getAccessTokenSilently();
      if (!tenantId) return;
      const response = await getReflectorIamPermissions(token, Number(reflector.id), tenantId);
      
      // Use centralized transformer
      const permissions = transformStandaloneIamPermissions(response);
      setIamPermissions(permissions);
    } catch (err) {
      setIamPermissionsError('Failed to load IAM permissions');
      console.error('Error fetching IAM permissions:', err);
    } finally {
      setIamPermissionsLoading(false);
    }
  };

  useEffect(() => {
    if (reflector?.id) {
      fetchIamPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reflector?.id, getAccessTokenSilently, tenantId]);

  // Track the last id we've fetched details for. Prevents an infinite refetch
  // loop caused by having `reflector` in the deps combined with a guard that
  // inspects possibly-falsy fields (`data`, `name`, etc.) on the fetched
  // object: when the backend returns e.g. `data: null` for a freshly cloned
  // reflector, the guard stays truthy after setReflector and re-triggers the
  // effect forever.
  const fetchedDetailsIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !tenantId) return;
    if (fetchedDetailsIdRef.current === id) return;
    fetchedDetailsIdRef.current = id;

    const fetchDetails = async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await getReflectorDetails(token, Number(id), tenantId);
        const transformedReflector = transformReflectorDetails(response.data);
        setReflector(transformedReflector);
      } catch (error) {
        console.error('Error fetching reflector details:', error);
        setDetailsError('Failed to load reflector details. Please try again.');
        // Allow a retry on next mount / id change if the fetch failed.
        fetchedDetailsIdRef.current = null;
      } finally {
        setDetailsLoading(false);
      }
    };
    fetchDetails();
  }, [id, tenantId, getAccessTokenSilently]);

  // Fetch BGP data when BGP tab is activated
  useEffect(() => {
    const fetchBgpData = async () => {
      // For cluster routes, always fetch data
      // For reflector routes, only fetch if not a cluster config
      const shouldFetch = isClusterRoute() || 
        (activeTab === 'BGP' && reflector?.id && !isClusterConfig(reflector));
      
      if (shouldFetch && activeTab === 'BGP' && reflector?.id) {
        setBgpLoading(true);
        setBgpError(null);
        try {
          const token = await getAccessTokenSilently();
          if (!tenantId) return;
          const configRes = await getReflectorBgpConfig(token, Number(reflector.id), tenantId);
          setBgpConfig(configRes.data ?? null);
        } catch (err) {
          // Check if it's a 404 error (no initial config) - don't set error in this case
          if (err instanceof AxiosError && err.response?.status === 404) {
            setBgpConfig(null); // Set to null to indicate no config exists
          } else {
            setBgpError('Failed to load BGP configuration');
          }
        } finally {
          setBgpLoading(false);
        }
      }
    };
    fetchBgpData();
  }, [activeTab, reflector?.id, getAccessTokenSilently, tenantId]);

  // Fetch Cloud Propagation data function (can be called manually for refresh)
  const fetchCloudPropagation = React.useCallback(async () => {
    // For cluster routes, always fetch data
    // For reflector routes, only fetch if not a cluster config
    const shouldFetch = isClusterRoute() || 
      (activeTab === 'Cloud Propagation' && reflector?.id && !isClusterConfig(reflector));
    
    if (shouldFetch && activeTab === 'Cloud Propagation' && reflector?.id) {
      setCloudPropagationLoading(true);
      setCloudPropagationError(null);
      try {
        const token = await getAccessTokenSilently();
        
        if (!tenantId) return;
        // If reflector is part of a cluster, fetch cluster propagation rules
        if (isClusterConfig(reflector) && reflector.clusterId) {
          const rulesRes = await getCloudPropagationRulesByCluster(token, Number(reflector.clusterId), tenantId);
          setCloudPropagationRules(rulesRes.data);
          setClusterIdFromResponse(Number(rulesRes.data.clusterId));
        } else {
          // Fetch individual reflector propagation rules
          const rulesRes = await getCloudPropagationRules(token, Number(reflector.id), tenantId);
          setCloudPropagationRules(rulesRes.data);
          setClusterIdFromResponse(undefined); // Clear clusterId if not a cluster config
        }
      } catch (err) {
        setCloudPropagationError('Failed to load Cloud Propagation rules');
      } finally {
        setCloudPropagationLoading(false);
      }
    }
  }, [activeTab, reflector?.id, reflector?.clusterId, getAccessTokenSilently, tenantId]);

  // Fetch Cloud Propagation data when Cloud Propagation tab is activated
  useEffect(() => {
    fetchCloudPropagation();
  }, [fetchCloudPropagation]);

  // Save handlers
  const handleSaveName = async () => {
    if (!reflector?.id) return;
    
    try {
      const token = await getAccessTokenSilently();
      if (!tenantId) return;
      await updateReflectorName(token, Number(reflector.id), { name: nameValue }, tenantId);
      setIsEditingName(false);
      // Refresh reflector details to get updated data
      const response = await getReflectorDetails(token, Number(reflector.id), tenantId);
      setReflector(prev => ({
        ...prev,
        ...response.data,
        name: nameValue,
      }));
    } catch (error) {
      console.error('Error updating reflector name:', error);
      // Revert to original name on error
      setNameValue(row?.name || '');
    }
  };

  const handleLegacyTagUpdate = useCallback(async (tags: string[]) => {
    if (!reflector?.id || !tenantId) return;
    const token = await getAccessTokenSilently();
    await updateReflectorTags(token, Number(reflector.id), { tags }, tenantId);
    const response = await getReflectorDetails(token, Number(reflector.id), tenantId);
    setReflector(transformReflectorDetails(response.data));
  }, [reflector?.id, tenantId, getAccessTokenSilently]);

  const handleCancelEditName = () => {
    setNameValue(row?.name || '');
    setIsEditingName(false);
  };

  // Search filters for CTXR Interfaces
  const [ctxrInterfacesFilters, setCtxrInterfacesFilters] = useState<ParsedFilter[]>([]);
  const ctxrInterfacesSuggestedKeys = ['Interface', 'IP Address', 'MAC Address', 'VPC ID', 'Cloud Subnet ID'];

  // Apply filters to data using utility functions for CTXR Interfaces
  const filteredInterfaces = filterInterfaces(reflector?.interfaces || [], ctxrInterfacesFilters);

  const navigate = useNavigate();

  // Refresh function for details and IAM permissions
  const handleRefreshDetails = async () => {
    if (!reflector?.id) return;

    setDetailsLoading(true);
    setDetailsError(null);
    try {
      // Refresh reflector details
      const token = await getAccessTokenSilently();
      if (!tenantId) return;
      const response = await getReflectorDetails(token, Number(reflector.id), tenantId);
      const transformedReflector = transformReflectorDetails(response.data);
      setReflector(transformedReflector);

      // Refresh IAM permissions
      await fetchIamPermissions();
    } catch (error) {
      console.error('Error refreshing details:', error);
      setDetailsError('Failed to load reflector details. Please try again.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleClusterSubmit = async (data: any) => {
    // The cluster has already been created by the modal
    // This callback is just to handle post-creation logic (refresh, etc.)
    // Set flag to refresh after modal closes (when alert is dismissed)
    setShouldRefreshAfterClusterCreation(true);
    
    // Don't close modal yet - let the success alert show first
    // The modal will close when user dismisses the alert
  };

  return (
    <div className="relative">
      <div className="mb-4 flex items-center gap-2">
        <GenericButton
          label="Back"
          variant="secondary"
          shape="ghost"
          size="sm"
          iconLeftName="arrowLeft"
          onClick={() => navigate('/reflector-v2/management/bgp-reflectors')}
        />
      </div>
      <div className="flex items-center justify-start mb-2 max-w-[800px]">
        <GenericTabs
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      {/* Details tab: first load and refresh both use skeleton (no spinner overlay) */}
      {/* Sin una licencia valida no se configura el reflector, y lo que quede a
          la vista se lee como ultimo estado conocido, no como dato vigente. Por
          eso el gate envuelve la ficha entera y no solo sus botones (CTX-525). */}
      {activeTab === 'Details' && (
        <LicenseGate gate={licenseGate}>
        <DataStateView
          loading={detailsLoading || isDetailsFirstLoad}
          error={detailsError}
          isEmpty={!reflector && !detailsLoading}
          onRetry={handleRefreshDetails}
          loadingContent={
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GenericCard isCollapsible={false} headerContent="Details">
                  <div className="divide-y divide-gray-100 p-4 space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <SkeletonLine key={i} w="100%" h={24} className="rounded" />
                    ))}
                  </div>
                </GenericCard>
                <GenericCard isCollapsible={false} headerContent="Interfaces">
                  <div className="p-4 space-y-3">
                    <SkeletonLine w="100%" h={40} className="rounded" />
                    <TableSkeleton columns={4} rows={4} />
                  </div>
                </GenericCard>
              </div>
            </div>
          }
          errorTitle="Failed to load reflector details"
          emptyTitle="No reflector details"
          emptyDescription="Reflector details could not be loaded."
        >
        <div className="space-y-4">
          {/* Top row: Reflector Details and Interfaces */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reflector Details Card */}
            <GenericCard
              isCollapsible={false}
              headerContent={
                <div className="flex items-center justify-between w-full">
                  <span>Details</span>
                  <GenericButton
                    icon={<IconRefresh className="w-4 h-4" />}
                    label="Sync"
                    variant="refresh"
                    shape="ghost"
                    size="sm"
                    aria-label="Refresh Details"
                    onClick={handleRefreshDetails}
                  />
                </div>
              }
            >
              <div className="divide-y divide-gray-100">
                {detailsSections[0]?.items.map((item, i) => {
                  // Editable logic for Name and Tag
                  if (item.label === 'Name') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center space-x-2 w-1/2 px-4 justify-end">
                          {isEditingName ? (
                            <>
                              <GenericButton
                                onClick={handleSaveName}
                                icon={<IconCheck className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Save Name"
                              />
                              <GenericButton
                                onClick={handleCancelEditName}
                                icon={<IconCancel className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Cancel Edit Name"
                              />
                              <input
                                type="text"
                                value={nameValue}
                                onChange={e => setNameValue(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                              />
                            </>
                          ) : (
                            <>
                              <GenericButton
                                onClick={() => setIsEditingName(true)}
                                icon={<IconEdit className="w-4 h-4" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Edit Name"
                              />
                              <span className="text-sm text-semantic-text-secondary">{nameValue}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (item.label === 'Tag') {
                    const legacyTags = Array.isArray(reflector?.tags) ? reflector.tags : [];
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center gap-2 w-1/2 px-4 justify-end">
                          <EditableTagList
                            tags={legacyTags}
                            onUpdate={handleLegacyTagUpdate}
                            resourceType="rv2-instance"
                            resourceId={reflector?.id ? Number(reflector.id) : null}
                            mount="reflector-v2"
                            tenantId={tenantId}
                            // Seeded from the response's rich `tags` array
                            // (TAGS-API-CONTRACT.md §5.5). Skips the §5.1
                            // round-trip on mount so the chip strip + modal
                            // render instantly. Falls back to §5.1 inside
                            // `useResourceTags` when the field isn't shipped.
                            initialAssignedTags={reflector?.tagsRich}
                          />
                        </div>
                      </div>
                    );
                  }
                  // Cluster as tag
                  if (item.label === 'Cluster') {
                    const clusterVal = String(item.value).trim();
                    const isUnset = !clusterVal || clusterVal === '-' || clusterVal === '—';
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          {isUnset ? (
                            <GenericButton 
                              label="Add Cluster" 
                              size="sm" 
                              variant="secondary" 
                              shape="ghost" 
                              onClick={() => setIsClusterModalOpen(true)} 
                            />
                          ) : (
                            <span 
                              className="cursor-pointer hover:opacity-80"
                              onClick={() => {
                                if (reflector?.clusterId) {
                                  navigate(`/reflector-v2/management/clusters/details/${reflector.clusterId}`);
                                }
                              }}
                            >
                              <GenericTag label={clusterVal} variant="neutral" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }
                  // Cloud as tag
                  if (item.label === 'Cloud') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant="warning" />
                        </div>
                      </div>
                    );
                  }
                  // Region as tag
                  if (item.label === 'Region') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant="neutral" />
                        </div>
                      </div>
                    );
                  }
                  // Status as tag
                  if (item.label === 'Status') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant={item.variant as any} />
                        </div>
                      </div>
                    );
                  }
                  // IAM Permissions as clickable tag
                  if (item.label === 'IAM Permissions') {
                    const { label: iamTagLabel, variant: iamTagVariant } = getIamTagState(iamPermissions);
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end space-x-2">
                          <span
                            className="cursor-pointer hover:opacity-80"
                            onClick={() => setIsIamModalOpen(true)}
                          >
                            <GenericTag label={iamTagLabel} variant={iamTagVariant as any} />
                          </span>
                          {/* <GenericButton
                            icon={<IconRefresh className="w-4 h-4" />}
                            label="Sync"
                            variant="refresh"
                            shape="ghost"
                            size="sm"
                            aria-label="Refresh IAM Permissions"
                            onClick={fetchIamPermissions}
                          /> */}
                          <IAMPermissionsModal 
                            isOpen={isIamModalOpen} 
                            onClose={() => setIsIamModalOpen(false)} 
                            permissions={iamPermissions}
                            loading={iamPermissionsLoading}
                            error={iamPermissionsError}
                          />
                        </div>
                      </div>
                    );
                  }
                  // Default rendering (plain text)
                  // Special handling for ID field in standalone reflector mode
                  if (item.label === 'ID' && !isClusterRoute() && !isClusterConfig(reflector)) {
                    const formattedId = formatReflectorId(reflector?.subId);
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          {formattedId ? (
                            <CopyableValue value={formattedId} />
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                      <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                      <div className="flex items-center w-1/2 px-4 justify-end">
                        {item.value ? (
                          // Use CopyableValue for ID fields
                          (item.label === 'ID' || item.label === 'UUID' || item.label === 'License ID' || item.label === 'VPC/VNET/VCN') ? (
                            <CopyableValue value={String(item.value)} />
                          ) : (
                            <span className="text-sm text-semantic-text-secondary">{item.value}</span>
                          )
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </GenericCard>

            {/* Interfaces Card */}
            <GenericCard
              isCollapsible={false}
              headerContent="Interfaces"
              className="flex flex-col"
            >
              <div className="py-2 px-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <GenericSearchbar
                      placeholder="Search CTXR Interfaces..."
                      suggestedKeys={ctxrInterfacesSuggestedKeys}
                      onFiltersChange={setCtxrInterfacesFilters}
                    />
                  </div>
                </div>
                <GenericTable
                  columns={ctxrInterfacesColumns}
                  data={filteredInterfaces}
                  initialRowsPerPage={getInitialRowsPerPage(filteredInterfaces.length)}
                  rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight={getTableBodyMaxHeight(INTERFACES_CHROME_PX)}
                />
              </div>
            </GenericCard>
          </div>
        </div>
        </DataStateView>
        </LicenseGate>
      )}
      {activeTab === 'BGP' && (
        // On cluster routes, always show the BGP content
        // On reflector routes, show alert if it's a cluster config
        isClusterRoute() ? (
          <>
            {bgpError ? (
              <EmptyItems />
            ) : (
              <LicenseGate gate={licenseGate}>
                <BgpDetailsPage
                  config={bgpPageConfig}
                  reflectorId={reflector?.id}
                  getAccessTokenSilently={getAccessTokenSilently}
                  context="reflector"
                  parentLoading={bgpLoading}
                  reflectorName={reflector?.name || row?.name || undefined}
                />
              </LicenseGate>
            )}
          </>
        ) : (
          reflector && isClusterConfig(reflector)
            ? <ClusterConfigAlert clusterName={reflector.cluster} clusterId={reflector.clusterId} />
            : (
              <>
                {bgpError ? (
                  <EmptyItems />
                ) : (
                  <LicenseGate gate={licenseGate}>
                    <BgpDetailsPage
                      config={bgpPageConfig}
                      reflectorId={reflector?.id}
                      getAccessTokenSilently={getAccessTokenSilently}
                      context="reflector"
                      parentLoading={bgpLoading}
                      reflectorName={reflector?.name || row?.name || undefined}
                    />
                  </LicenseGate>
                )}
              </>
            )
        )
      )}
      {activeTab === 'Cloud Propagation' && (
        // On cluster routes, always show the table
        // On reflector routes, show alert if it's a cluster config
        isClusterRoute() ? (
          <LicenseGate gate={licenseGate}>
            <CloudPropagationPage
              data={cloudPropagationRules}
              columns={cloudPropagationColumns}
              error={cloudPropagationError}
              getAccessTokenSilently={getAccessTokenSilently}
              clusterId={clusterIdFromResponse}
              onRefresh={fetchCloudPropagation}
              loading={cloudPropagationLoading}
              instanceLimits={(bgpPageConfig as { reflectorInstance?: { instanceLimits?: unknown } })?.reflectorInstance?.instanceLimits}
            />
          </LicenseGate>
        ) : (
          reflector && isClusterConfig(reflector)
            ? <ClusterConfigAlert clusterName={reflector.cluster} clusterId={reflector.clusterId} />
            : (
              <LicenseGate gate={licenseGate}>
                <CloudPropagationPage
                  data={cloudPropagationRules}
                  columns={cloudPropagationColumns}
                  error={cloudPropagationError}
                  getAccessTokenSilently={getAccessTokenSilently}
                  clusterId={clusterIdFromResponse}
                  reflectorId={reflector?.id ? Number(reflector.id) : undefined}
                  onRefresh={fetchCloudPropagation}
                  loading={cloudPropagationLoading}
                  instanceLimits={(bgpPageConfig as { reflectorInstance?: { instanceLimits?: unknown } })?.reflectorInstance?.instanceLimits}
                  disableDynamicTagOptions={isVersionBelow(reflector?.version)}
                  disableNexthopRetry={isVersionBelow(reflector?.version, NEXTHOP_RETRY_MIN_VERSION)}
                  useNexthopVerification={!isVersionBelow(reflector?.version, NEXTHOP_WATCH_MIN_VERSION)}
                />
              </LicenseGate>
            )
        )
      )}
      {activeTab === 'Cloud Elements' && reflector?.id && (
        <LicenseGate gate={licenseGate}>
        <CloudElementsPage
          columns={cloudElementsColumns as unknown as CloudElementsPageProps['columns']}
          suggestedKeys={[...CLOUD_ELEMENTS_SUGGESTED_KEYS_REFLECTOR]}
          reflectorId={Number(reflector.id)}
          getAccessTokenSilently={getAccessTokenSilently}
        />
        </LicenseGate>
      )}
      {activeTab === 'Syslog' && (
        reflector && !isClusterConfig(reflector) ? (
          <LicenseGate gate={licenseGate}>
            <SyslogPage
              ctxrId={reflector.id}
              getAccessTokenSilently={getAccessTokenSilently}
            />
          </LicenseGate>
        ) : (
          reflector && isClusterConfig(reflector) ? (
            <ClusterConfigAlert clusterName={reflector.cluster} clusterId={reflector.clusterId} />
          ) : null
        )
      )}

      <ClusterModal
        isOpen={isClusterModalOpen}
        onClose={async () => {
          const needsRefresh = shouldRefreshAfterClusterCreation;
          setIsClusterModalOpen(false);
          
          // Refresh reflector details after modal closes (alert was dismissed)
          if (needsRefresh) {
            setShouldRefreshAfterClusterCreation(false);
            await handleRefreshDetails();
          }
        }}
        onSubmit={handleClusterSubmit}
      />
    </div>
  );
}

export default ReflectorDetailsPage;